"use client";

import Link from "next/link";
import { ArrowRight, Check } from "lucide-react";

import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import { getLocalizedPathname } from "@/lib/i18n";
import { whatsappMessage } from "@/lib/whatsapp";
import { WhatsAppCta } from "@/components/shared/WhatsAppCta";

// Local-partner CTA for every SonarQube blog post (Jazz, 2026-10-07: "should say we
// are local partner, support ringgit and sgd currency, call us"). One component so
// the five posts say the same thing. "band" closes a post; "inline" sits after the
// intro so the offer is visible without scrolling to the end.
const MY_QUOTE = "/enterprise/enquiry/?brand=sonarqube-malaysia";
// The SG section is English-only: a plain href, never localized.
const SG_QUOTE = "/singapore/sonarqube/#quote";

interface SonarPartnerCTAProps {
  /** Page name for the WhatsApp message and GA4 location, e.g. "the RMiT article". */
  source: string;
  /** GA4 "Click location" prefix, e.g. "rmit_blog". */
  location: string;
  variant?: "band" | "inline";
  /** Plain purchasing copy for readers comparing where to buy. */
  audience?: "technical" | "buyer";
  /** Optional topic-specific heading for the band (already English; translated via t). */
  title?: string;
}

const POINTS = [
  "Quotes in Ringgit (RM) or Singapore dollars (SGD)",
  "Local setup on your repositories and CI/CD",
  "Team training, HRD Corp claimable in Malaysia",
];

const BUYER_POINTS = [
  "RM quotes for Malaysia and SGD quotes for Singapore",
  "Setup help, quoted separately",
  "Developer training if your team needs it",
];

export default function SonarPartnerCTA({
  source,
  location,
  variant = "band",
  audience = "technical",
  title = "Buy SonarQube from a local partner, in Ringgit or SGD",
}: SonarPartnerCTAProps) {
  const locale = useLocale();
  // ?from= tells the enquiry form (and GA4) which post sent the buyer.
  const myQuote = getLocalizedPathname(`${MY_QUOTE}&from=${location}_rm`, locale);
  const wa = whatsappMessage("I'd like a SonarQube quote", source);
  const isBuyer = audience === "buyer";
  const points = isBuyer ? BUYER_POINTS : POINTS;

  if (variant === "inline") {
    return (
      <aside className="not-prose my-8 rounded-2xl border border-primary/20 bg-primary/5 p-5 sm:p-6">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">
          {t(locale, "Your local Sonar reseller partner")}
        </p>
        <p className="mt-2 text-base leading-relaxed text-neutral-emphasis">
          {t(
            locale,
            isBuyer
              ? "We can quote your SonarQube licence in RM or SGD, and include setup or training if you need it."
              : "We supply SonarQube, Advanced Security, Gitar and the AI agents in Malaysia and Singapore, quoted in Ringgit or SGD, with local setup and support.",
          )}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-semibold">
          <Link href={myQuote} className="inline-flex items-center gap-1 text-primary hover:underline"
            data-track-event="cta_click" data-track-location={`${location}_inline_rm`} data-track-market="my">
            {t(locale, "Get a quote in RM")} <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
          <Link href={SG_QUOTE} className="inline-flex items-center gap-1 text-primary hover:underline"
            data-track-event="cta_click" data-track-location={`${location}_inline_sgd`} data-track-market="sg">
            {t(locale, "Get a quote in SGD")} <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      </aside>
    );
  }

  return (
    <section className="not-prose my-12 overflow-hidden rounded-3xl bg-hero-background px-6 py-10 md:px-12 md:py-14">
      <p className="text-sm font-semibold uppercase tracking-[0.3em] text-primary-contrast/60">
        {t(locale, "Your local Sonar reseller partner")}
      </p>
      <h3 className="mt-4 font-display text-2xl font-bold tracking-tight text-primary-contrast md:text-3xl">
        {t(locale, title)}
      </h3>
      <p className="mt-4 max-w-2xl text-primary-contrast/75">
        {t(
          locale,
          isBuyer
            ? "Tell us which company is buying, whether this is a new order or renewal, and where you need help."
            : "Anchor Sprint is a Sonar reseller partner in Malaysia and Singapore. Talk to us about SonarQube Cloud or Server licences, Advanced Security, Gitar and the AI agents, renewals, or a free review of what your team needs.",
        )}
      </p>
      <ul className="mt-6 space-y-2">
        {points.map((p) => (
          <li key={p} className="flex items-start gap-3 text-sm text-primary-contrast/85">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
            {t(locale, p)}
          </li>
        ))}
      </ul>
      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <Link
          href={myQuote}
          data-track-event="cta_click"
          data-track-location={`${location}_band_rm`}
          data-track-market="my"
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary px-7 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
        >
          {t(locale, "Get a quote in RM")}
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
        <Link
          href={SG_QUOTE}
          data-track-event="cta_click"
          data-track-location={`${location}_band_sgd`}
          data-track-market="sg"
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-primary-contrast/30 px-7 py-3 text-sm font-semibold text-primary-contrast transition hover:bg-primary-contrast/10"
        >
          {t(locale, "Get a quote in SGD")}
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
        <WhatsAppCta message={wa} location={`${location}_cta`} label={t(locale, "WhatsApp us")} />
      </div>
    </section>
  );
}
